import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { PROGRAMS } from "@/OS/utils/programs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const folderOrder = ["AI", "Development", "System", "Office", "Explore", "Games"];
const ungroupedPrograms = PROGRAMS.filter((program) => !program.folder).sort((a, b) => a.label.localeCompare(b.label));

const groupedPrograms = [
  ...(ungroupedPrograms.length ? [{ folder: "Core", programs: ungroupedPrograms }] : []),
  ...folderOrder.map((folder) => ({
    folder,
    programs: PROGRAMS.filter((program) => program.folder === folder).sort((a, b) => a.label.localeCompare(b.label)),
  })),
].filter(({ programs }) => programs.length > 0);

const appCount = PROGRAMS.length;

export default function ArchitectureTeaser() {
  return (
    <section id="current-app-ecosystem" className="scroll-mt-24 px-6 py-20 md:py-28">
      <div className="mx-auto max-w-7xl">
        <Card className="overflow-hidden rounded-[32px] border-white/10 bg-[linear-gradient(180deg,rgba(8,12,22,0.96),rgba(3,7,18,1))] text-white shadow-[0_25px_100px_rgba(0,0,0,0.3)]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55 }}
            className="p-6 md:p-8 lg:p-10"
          >
            <div className="mb-5 flex items-center justify-between gap-3 border-b border-white/10 pb-5">
              <div className="flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-primary/80">
                <img
                  src="/os/assets/icons/all-programs.svg"
                  alt="All Programs"
                  className="h-4 w-4 object-contain"
                  loading="lazy"
                />
                current app ecosystem
              </div>
              <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/55 sm:flex">
                <Info className="h-3.5 w-3.5" /> click any app
              </div>
            </div>

            <div className="overflow-hidden rounded-[28px] border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(132,255,76,0.1),transparent_26%),linear-gradient(180deg,rgba(4,9,18,0.98),rgba(5,8,14,0.96))] shadow-[0_18px_50px_rgba(0,0,0,0.24)]">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 text-xs uppercase tracking-[0.28em] text-white/55">
                <div className="flex items-center gap-3">
                  <span className="h-3 w-3 rounded-full bg-primary/90 shadow-[0_0_10px_hsl(var(--primary)/0.45)]" />
                  <span>mapleos apps</span>
                </div>
                <div className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] tracking-[0.2em] text-white/55">
                  {appCount} total
                </div>
              </div>

              <div className="max-h-[46rem] overflow-y-auto px-4 py-5 sm:px-5 md:px-6">
                {groupedPrograms.map(({ folder, programs }) => (
                  <div key={folder} className="mb-8 last:mb-0">
                    <div className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-primary/80">
                      <span>{folder}</span>
                      <span className="h-px flex-1 bg-white/8" />
                      <span className="text-white/35">{programs.length}</span>
                    </div>

                    <div className="grid grid-cols-3 gap-x-3 gap-y-6 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7">
                      {programs.map((program) => {
                        const Icon = program.icon;

                        return (
                          <Dialog key={program.id}>
                            <DialogTrigger asChild>
                              <button
                                type="button"
                                className="group flex flex-col items-center px-1 py-1 text-center outline-none transition-transform duration-300 hover:-translate-y-1 focus-visible:-translate-y-1"
                                aria-label={`Open details for ${program.label}`}
                              >
                                <div className="relative flex h-20 w-20 items-center justify-center sm:h-24 sm:w-24">
                                  <div className="absolute inset-x-4 bottom-2 h-5 rounded-full bg-primary/12 blur-xl" />
                                  <Icon size={56} className="relative z-10 h-14 w-14 object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,0.34)] transition-transform duration-300 group-hover:scale-[1.05] sm:h-16 sm:w-16" />
                                </div>
                                <span className="mt-2 max-w-[7rem] text-[11px] font-medium leading-4 text-white/78 transition-colors duration-300 group-hover:text-white sm:text-xs">
                                  {program.label}
                                </span>
                              </button>
                            </DialogTrigger>

                            <DialogContent className="border-white/10 bg-[linear-gradient(180deg,rgba(7,12,22,0.98),rgba(4,7,14,1))] p-0 text-white shadow-[0_30px_120px_rgba(0,0,0,0.55)] sm:max-w-md">
                              <div className="overflow-hidden rounded-lg">
                                <div className="border-b border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(132,255,76,0.12),transparent_34%),linear-gradient(180deg,rgba(12,18,28,0.94),rgba(7,12,20,0.94))] px-6 py-4">
                                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-primary/80">
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary/90 shadow-[0_0_10px_hsl(var(--primary)/0.45)]" />
                                    {(program.folder ?? "Core")} app
                                  </div>
                                </div>

                                <div className="p-6">
                                  <div className="flex items-start gap-4">
                                    <div className="relative flex h-20 w-20 shrink-0 items-center justify-center">
                                      <div className="absolute inset-x-3 bottom-2 h-5 rounded-full bg-primary/14 blur-xl" />
                                      <Icon size={60} className="relative z-10 h-16 w-16 object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,0.34)]" />
                                    </div>

                                    <DialogHeader className="space-y-2 text-left">
                                      <DialogTitle className="text-2xl font-semibold tracking-[-0.04em] text-white">
                                        {program.label}
                                      </DialogTitle>
                                      <div className="inline-flex w-fit rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-white/55">
                                        {program.folder ?? "Core"}
                                      </div>
                                      <DialogDescription className="max-w-sm text-sm leading-7 text-white/65">
                                        {program.description ?? "A MapleOS application available from the start menu."}
                                      </DialogDescription>
                                    </DialogHeader>
                                  </div>
                                </div>

                                <div className="border-t border-white/10 bg-white/[0.02] px-6 py-4">
                                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                    <div>
                                      <p className="text-sm font-medium text-white">Explore the full app guide</p>
                                      <p className="text-xs text-white/55">Docs open in a new content view.</p>
                                    </div>
                                    <Button asChild variant="link" className="h-auto justify-start px-0 text-primary sm:justify-end">
                                      <Link to={`/docs?app=${program.id}`} aria-label={`Learn more about ${program.label} in the docs`}>
                                        Learn more
                                        <ExternalLink className="ml-2 h-4 w-4" />
                                      </Link>
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </Card>
      </div>
    </section>
  );
}